/**
 * TodayPage Component
 * Main home view showing today's meals and weekly preview
 *
 * Features:
 * - Today's meal cards (with rating, viewing, planning)
 * - Weekly preview with day selection
 * - Empty states with CTAs
 */

import { useState, useMemo, useEffect } from 'react'
import { useUserId, useAuth, useTodayMeals, formatDateKey, formatDisplayDate, getWeekDates } from '@talktoast/shared'
import { favoritesApiService } from '@talktoast/shared'
import { MealAssignment, MealType, RecipeDetailResponse } from '@talktoast/shared'
import { Button, Card, Loading } from '@talktoast/ui'
import { ICONS, getIconSize, getIconColor } from '@talktoast/shared'
import RecipeCard, { Recipe } from '../components/RecipeCard'
import WeeklyPreview from '../components/WeeklyPreview'
import { showError, showSuccess } from '../utils/toast'
import { MEAL_TYPE_ORDER } from '../constants/mealTiming'

export interface TodayPageProps {
  /**
   * Callback when user wants to start planning.
   * When forceWeeklyDefaults is true, SessionPreferences should ignore saved
   * meal counts and use the 5-day workweek defaults instead.
   */
  onStartPlanning: (mealType?: MealType, servings?: number, forceWeeklyDefaults?: boolean) => void

  /**
   * Callback when user wants to view auth screens
   */
  onShowAuth?: (authType: 'login' | 'signup') => void
}

export default function TodayPage({
  onStartPlanning,
  onShowAuth
}: TodayPageProps) {
  const userId = useUserId()
  const { isGuest } = useAuth()
  const { todayMeals, weekSummary, weekRecipes, isLoading, isError } = useTodayMeals(userId)

  // State for week navigation
  const [displayDate, setDisplayDate] = useState<string>(formatDateKey(new Date()))
  const [expandedMealId, setExpandedMealId] = useState<string | null>(null)

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  // Filter meals by selected date
  const displayedMeals = useMemo(() => {
    if (!weekRecipes) return []

    return weekRecipes
      .filter(r => r.assigned_dates?.includes(displayDate))
      .map(r => ({
        id: `${r.id}_${displayDate}`,
        recipeId: r.id,
        recipeName: r.recipe_data.name,
        mealType: r.recipe_data.mealType as MealType,
        date: displayDate,
        createdAt: r.created_at,
        updatedAt: r.updated_at
      }))
      .sort((a, b) => MEAL_TYPE_ORDER[a.mealType] - MEAL_TYPE_ORDER[b.mealType])
  }, [weekRecipes, displayDate])

  // Generate week dates for WeeklyPreview
  const weekDates = useMemo(() => {
    return getWeekDates(0).map(date => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const isToday = date.toDateString() === today.toDateString()
      const dayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][date.getDay()]
      const dayLabel = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]
      const fullLabel = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()]

      return {
        key: dayKey,
        label: dayLabel,
        fullLabel,
        date: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear(),
        fullDate: date,
        isToday
      }
    })
  }, [])

  // === CALLBACKS ===

  /**
   * Handle planning a specific meal type
   */
  const handlePlan = (mealType: MealType) => {
    onStartPlanning(mealType, 1) // Pre-select meal type and 1 serving
  }

  /**
   * Handle viewing full recipe details (accordion expand)
   */
  const handleViewRecipe = (meal: MealAssignment) => {
    setExpandedMealId(meal.id === expandedMealId ? null : meal.id)
  }

  /**
   * Handle rating a meal (thumbs up/down)
   */
  const handleRate = async (meal: MealAssignment, rating: 'up' | 'down') => {
    if (!userId) {
      showError('Please sign in to rate meals')
      return
    }

    // Find recipe in already-loaded week data (NO API CALL!)
    const recipe = weekRecipes?.find(r => r.id === meal.recipeId)

    if (!recipe) {
      console.error('[TodayPage] Recipe not found in cache:', meal.recipeId)
      showError('Recipe not found. Please refresh the page.')
      return
    }

    try {
      await favoritesApiService.updateRecipeRating(userId, {
        recipeHash: favoritesApiService.generateRecipeHash(meal.recipeName),
        recipeName: meal.recipeName,
        sessionId: recipe.session_id || undefined,
        thumbsRating: rating === 'up' ? 1 : -1
        // NOTE: isFavorite is NOT set here - thumbs up/down are separate from favorites
      })

      showSuccess(rating === 'up' ? 'Recipe liked!' : 'Noted!')
    } catch (error) {
      console.error('[TodayPage] Failed to rate recipe:', error)
      showError('Failed to save rating. Please try again.')
    }
  }

  /**
   * Handle clicking on a day in the week preview
   */
  const handleDayClick = (dateStr: string) => {
    setDisplayDate(dateStr)
    setExpandedMealId(null) // Collapse any expanded meals
  }

  /**
   * Handle planning dinner for tonight (quick action)
   */
  const handlePlanDinner = () => {
    onStartPlanning('dinner', 1)
  }

  /**
   * Handle planning the full week (general action).
   * Always forces 5-day workweek defaults regardless of saved session preferences.
   */
  const handlePlanWeek = () => {
    onStartPlanning(undefined, undefined, true) // Force weekly defaults
  }

  /**
   * Handle clicking "Back to Today" button
   */
  const handleBackToToday = () => {
    setDisplayDate(formatDateKey(new Date()))
    setExpandedMealId(null)
  }

  // === RENDER ===

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loading variant="spinner" size="md" className="mx-auto" />
          <p className="text-gray-500 mt-2">Loading your meals...</p>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="p-8 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <ICONS.feedback.error className={`${getIconSize('xl')} ${getIconColor('error')}`} />
          </div>
          <h3 className="text-h3 text-gray-900 mb-2">Unable to load meals</h3>
          <p className="text-body-sm text-gray-600 mb-4">
            There was a problem loading your meal plan. Please try refreshing the page.
          </p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </Card>
      </div>
    )
  }

  const isViewingToday = displayDate === formatDateKey(new Date())

  return (
    <div className="spacing-page">
        {/* TODAY SECTION */}
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Today</h2>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* Contextual badge - only shows when NOT viewing today */}
        {!isViewingToday && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <span className="text-body-sm text-blue-900">
              Viewing <strong>{formatDisplayDate(displayDate)}</strong>
            </span>
            <button
              onClick={handleBackToToday}
              className="ml-auto text-body-sm text-blue-600 font-medium hover:underline"
            >
              ← Back to Today
            </button>
          </div>
        )}

        {/* Meal Cards */}
        {displayedMeals.length > 0 ? (
          <div className="space-y-4 mb-6">
            {displayedMeals.map((meal) => {
              const fullRecipe = weekRecipes?.find(r => r.id === meal.recipeId)

              if (!fullRecipe) return null

              // Convert to Recipe format for RecipeCard
              const recipe: Recipe = {
                id: meal.recipeId,
                name: meal.recipeName,
                mealType: meal.mealType,
                protein: fullRecipe.recipe_data.protein,
                cookingMethod: fullRecipe.recipe_data.cookingMethod,
                cuisine: fullRecipe.recipe_data.cuisine
              }

              // Build RecipeDetailResponse for expanded view
              const recipeDetails: RecipeDetailResponse = {
                recipe: fullRecipe.recipe_data,
                sessionId: fullRecipe.session_id,
                kitchenTools: [],
                cookingTips: []
              }

              return (
                <RecipeCard
                  key={meal.id}
                  recipe={recipe}
                  recipeDetails={recipeDetails}
                  variant="history"
                  description={fullRecipe.recipe_data.description}
                  prepTime={fullRecipe.recipe_data.prepTime}
                  cookTime={fullRecipe.recipe_data.cookTime}
                  servings={fullRecipe.recipe_data.servings}
                  dateUsed={meal.date}
                  thumbsRating={0} // TODO: Get from favorites API
                  isFavorite={false} // TODO: Get from favorites API
                  isExpanded={meal.id === expandedMealId}
                  onToggleExpanded={(recipeId) => {
                    // Toggle: if already expanded, collapse; otherwise expand this one
                    setExpandedMealId(meal.id === expandedMealId ? null : meal.id)
                  }}
                  onThumbsUp={() => handleRate(meal, 'up')}
                  onThumbsDown={() => handleRate(meal, 'down')}
                  onToggleFavorite={() => {
                    // TODO: Implement favorite toggle
                  }}
                />
              )
            })}
          </div>
        ) : (
          <Card className="p-4 bg-gray-50 border-2 border-dashed border-gray-300">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <ICONS.navigation.calendarDays className={`${getIconSize('lg')} ${getIconColor('muted')}`} />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                {isViewingToday ? 'No meals planned for today' : 'No meals planned for this day'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Get a quick start with dinner, or plan your whole week
              </p>
              <Button variant="primary" onClick={handlePlanDinner} size="sm">
                <ICONS.navigation.add className={`${getIconSize('sm')} mr-1.5`} />
                {isViewingToday ? 'Plan Dinner for Tonight' : `Plan Dinner for ${formatDisplayDate(displayDate)}`}
              </Button>
            </div>
          </Card>
        )}

      {/* THIS WEEK SECTION */}
      <div className="flex items-center gap-2 mb-3 mt-6">
        <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">This Week</h2>
        <div className="flex-1 h-px bg-gray-200"></div>
      </div>

        {(() => {
          // Count how many days have meals (excluding today)
          const todayKey = formatDateKey(new Date())
          const otherDaysWithMeals = weekSummary.filter(day => day.date !== todayKey && day.mealCount > 0)
          const hasOtherMeals = otherDaysWithMeals.length > 0

          // Show CTA if: (1) only today has meals OR (2) entire week is empty
          const shouldShowCTA = !hasOtherMeals

          return shouldShowCTA ? (
            <Card className="p-4 bg-gray-50 border-2 border-dashed border-gray-300">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <ICONS.utility.clock className={`${getIconSize('lg')} ${getIconColor('muted')}`} />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {displayedMeals.length > 0 ? 'Plan the rest of your week' : 'Plan your week'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {displayedMeals.length > 0
                    ? 'You have meals for today - keep the momentum going!'
                    : 'Get organized with a full week of meal plans'
                  }
                </p>
                <Button variant="primary" onClick={handlePlanWeek} size="sm">
                  <ICONS.navigation.add className={`${getIconSize('sm')} mr-1.5`} />
                  Plan Your Week
                </Button>
              </div>
            </Card>
          ) : (
            <WeeklyPreview
              weekDates={weekDates}
              weekSummary={weekSummary}
              selectedDate={displayDate}
              onDayClick={handleDayClick}
            />
          )
        })()}
    </div>
  )
}
